"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isDirective = isDirective;
exports.isLiteralDirective = isLiteralDirective;
exports.isTemplateDirective = isTemplateDirective;
exports.getFieldValue = getFieldValue;
exports.isPathDirective = isPathDirective;
exports.isIfDirective = isIfDirective;
exports.isArrayPathDirective = isArrayPathDirective;
exports.isCaseDirective = isCaseDirective;
exports.isReplaceDirective = isReplaceDirective;
exports.isJSONDirective = isJSONDirective;
exports.isFlattenDirective = isFlattenDirective;
exports.isMergeDirective = isMergeDirective;
exports.isTransformDirective = isTransformDirective;
exports.isExcludeWhenNullDirective = isExcludeWhenNullDirective;
exports.isLiquidDirective = isLiquidDirective;
exports.getFieldValueKeys = getFieldValueKeys;
exports.getRawKeys = getRawKeys;
exports.getTemplateKeys = getTemplateKeys;
const liquid_directive_1 = require("./liquid-directive");
function isObject(value) {
    return value !== null && typeof value === 'object';
}
function isDirective(value) {
    return (value !== null &&
        typeof value === 'object' &&
        Object.keys(value).some((key) => [
            '@if',
            '@path',
            '@template',
            '@literal',
            '@arrayPath',
            '@case',
            '@replace',
            '@json',
            '@flatten',
            '@merge',
            '@transform',
            '@excludeWhenNull',
            '@liquid'
        ].includes(key)));
}
function isLiteralDirective(value) {
    return isDirective(value) && '@literal' in value;
}
function isTemplateDirective(value) {
    return isDirective(value) && '@template' in value;
}
function getFieldValue(value) {
    if (isTemplateDirective(value)) {
        return value['@template'];
    }
    return value;
}
function isPathDirective(value) {
    return isDirective(value) && '@path' in value;
}
function isIfDirective(value) {
    return (isDirective(value) &&
        '@if' in value &&
        value['@if'] !== null &&
        typeof value['@if'] === 'object' &&
        ('exists' in value['@if'] || 'blank' in value['@if']));
}
function isArrayPathDirective(value) {
    return isDirective(value) && '@arrayPath' in value && Array.isArray(value['@arrayPath']);
}
function isCaseDirective(value) {
    return (isDirective(value) &&
        '@case' in value &&
        value['@case'] !== null &&
        typeof value['@case'] === 'object' &&
        'operator' in value['@case']);
}
function isReplaceDirective(value) {
    return (isDirective(value) &&
        '@replace' in value &&
        value['@replace'] !== null &&
        typeof value['@replace'] === 'object' &&
        'pattern' in value['@replace']);
}
function isJSONDirective(value) {
    return (isDirective(value) &&
        '@json' in value &&
        value['@json'] !== null &&
        typeof value['@json'] === 'object' &&
        'value' in value['@json']);
}
function isFlattenDirective(value) {
    return (isDirective(value) &&
        '@flatten' in value &&
        value['@flatten'] !== null &&
        typeof value['@flatten'] === 'object' &&
        'value' in value['@flatten']);
}
function isMergeDirective(value) {
    return (isDirective(value) &&
        '@merge' in value &&
        value['@merge'] !== null &&
        typeof value['@merge'] === 'object' &&
        'objects' in value['@merge']);
}
function isTransformDirective(value) {
    return (isDirective(value) &&
        '@transform' in value &&
        value['@transform'] !== null &&
        typeof value['@transform'] === 'object' &&
        'apply' in value['@transform'] &&
        'mapping' in value['@transform']);
}
function isExcludeWhenNullDirective(value) {
    return isDirective(value) && '@excludeWhenNull' in value;
}
function isLiquidDirective(value) {
    return (isDirective(value) && '@liquid' in value && typeof value['@liquid'] === 'string' && value['@liquid'] !== undefined);
}
function directiveType(directive, checker) {
    if (isArrayPathDirective(directive)) {
        return checker['@arrayPath'](directive);
    }
    if (isCaseDirective(directive)) {
        return checker['@case'](directive);
    }
    if (isIfDirective(directive)) {
        return checker['@if'](directive);
    }
    if (isLiteralDirective(directive)) {
        return checker['@literal'](directive);
    }
    if (isPathDirective(directive)) {
        return checker['@path'](directive);
    }
    if (isReplaceDirective(directive)) {
        return checker['@replace'](directive);
    }
    if (isTemplateDirective(directive)) {
        return checker['@template'](directive);
    }
    if (isJSONDirective(directive)) {
        return checker['@json'](directive);
    }
    if (isFlattenDirective(directive)) {
        return checker['@flatten'](directive);
    }
    if (isExcludeWhenNullDirective(directive)) {
        return checker['@excludeWhenNull'](directive);
    }
    if (isLiquidDirective(directive)) {
        return checker['@liquid'](directive);
    }
    return null;
}
function getFieldValueKeys(value) {
    if (isDirective(value)) {
        return (directiveType(value, {
            '@arrayPath': (input) => input['@arrayPath'].flatMap(getRawKeys),
            '@case': (input) => getRawKeys(input['@case'].value),
            '@if': (input) => [
                ...getRawKeys(input['@if'].blank),
                ...getRawKeys(input['@if'].exists),
                ...getRawKeys(input['@if'].then),
                ...getRawKeys(input['@if'].else)
            ],
            '@literal': (_) => [''],
            '@path': (input) => [input['@path']],
            '@replace': (input) => getRawKeys(input['@replace'].value),
            '@template': (input) => getTemplateKeys(input['@template']),
            '@json': (input) => getRawKeys(input['@json'].value),
            '@flatten': (input) => getRawKeys(input['@flatten'].value),
            '@merge': (input) => getRawKeys(input['@merge'].objects),
            '@transform': (input) => [
                ...getRawKeys(input['@transform'].apply),
                ...getRawKeys(input['@transform'].mapping)
            ],
            '@excludeWhenNull': (_) => [''],
            '@liquid': (input) => (0, liquid_directive_1.getLiquidKeys)(input['@liquid'])
        })?.filter((k) => k) ?? []);
    }
    else if (isObject(value)) {
        return Object.values(value).flatMap(getFieldValueKeys);
    }
    return [];
}
function getRawKeys(input) {
    if (isDirective(input)) {
        return getFieldValueKeys(input);
    }
    else if (isObject(input)) {
        return Object.values(input).flatMap(getFieldValueKeys);
    }
    return [];
}
function getTemplateKeys(input) {
    const regex = /{{(.*?)}}/g;
    return Array.from(input.matchAll(regex), (m) => m[1]);
}
//# sourceMappingURL=value-keys.js.map